import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EVENT_CARD_GRID_RESIZE_DEBOUNCE_MS,
  buildEventCardPageSizeOptions,
  getEventCardGridMetrics,
  resolveClosestEventPageSize,
} from './eventCardGridMetrics';

export default function useEventCardGridLayout({
  active,
  metricsOptions,
  basePageSizeOptions,
  currentPage,
  isCompactCardLayout,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  viewMode,
}) {
  // 既定はイベントカード表示（viewMode==='card'）。他画面は active を明示して流用する。
  const isActive = active !== undefined ? active : viewMode === 'card';
  const metricMinCardWidth = metricsOptions?.minCardWidth;
  const metricGap = metricsOptions?.gap;
  const [gridNode, setGridNode] = useState(null);
  const [columnCount, setColumnCount] = useState(() =>
    getEventCardGridMetrics(0, {
      isCompact: isCompactCardLayout,
      minCardWidth: metricMinCardWidth,
      gap: metricGap,
    }).columnCount
  );

  const cardGridRef = useCallback((node) => {
    setGridNode(node);
  }, []);

  useEffect(() => {
    if (!isActive || !gridNode) return undefined;

    let timerId = null;
    const updateColumns = () => {
      const width = gridNode.getBoundingClientRect?.().width || 0;
      const next = getEventCardGridMetrics(width, {
        isCompact: isCompactCardLayout,
        minCardWidth: metricMinCardWidth,
        gap: metricGap,
      }).columnCount;
      setColumnCount((prev) => (prev === next ? prev : next));
    };
    const scheduleUpdate = () => {
      if (timerId) window.clearTimeout(timerId);
      timerId = window.setTimeout(updateColumns, EVENT_CARD_GRID_RESIZE_DEBOUNCE_MS);
    };

    updateColumns();

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(gridNode);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', scheduleUpdate);
    }

    return () => {
      if (timerId) window.clearTimeout(timerId);
      if (observer) observer.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', scheduleUpdate);
      }
    };
  }, [gridNode, isCompactCardLayout, isActive, metricMinCardWidth, metricGap]);

  const pageSizeOptions = useMemo(() => {
    if (!isActive) return basePageSizeOptions;
    return buildEventCardPageSizeOptions(basePageSizeOptions, columnCount);
  }, [basePageSizeOptions, columnCount, isActive]);

  useEffect(() => {
    const nextSize = resolveClosestEventPageSize(itemsPerPage, pageSizeOptions, basePageSizeOptions[0]);
    if (nextSize === itemsPerPage) return;
    const safePage = Math.max(1, Number(currentPage) || 1);
    const firstIndex = (safePage - 1) * itemsPerPage;
    setItemsPerPage(nextSize);
    setCurrentPage(Math.max(1, Math.floor(firstIndex / nextSize) + 1));
  }, [
    basePageSizeOptions,
    currentPage,
    itemsPerPage,
    pageSizeOptions,
    setCurrentPage,
    setItemsPerPage,
  ]);

  const cardGridStyle = useMemo(() => ({
    '--event-card-columns': columnCount,
  }), [columnCount]);

  return {
    cardGridRef,
    cardGridStyle,
    eventCardColumnCount: columnCount,
    pageSizeOptions,
  };
}
